import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RotateCw, Sun, Contrast, RotateCcw, Undo2, Wand2, Loader2, Download, Cloud, Sparkles, Square, Circle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { amplifyApi } from '../services/amplifyApi';
import awsExports from '../aws-exports';

// Helper to get API endpoint from aws-exports (supports multi-env)
const getApiEndpoint = () => awsExports.aws_cloud_logic_custom?.[0]?.endpoint || 'https://53ogiynuu2.execute-api.us-east-1.amazonaws.com/dev';

// Display canvas size for smooth editing UX
const DISPLAY_MAX_SIZE = 800;
// Maximum export size to prevent memory issues
const EXPORT_MAX_SIZE = 4000;
// Print quality and API size thresholds
const MIN_PRINT_SIZE = 1500;  // Minimum px for acceptable print quality (300 DPI for 5" print)
const MAX_BG_REMOVAL_SIZE = 4000;  // Maximum px for background removal API
const OPTIMAL_BG_REMOVAL_SIZE = 3500;  // Target size for resize (safe margin under 10MB)

const ImageEditor = ({ image, onSave, onCancel, onRemoveBackground, onSaveDesign }) => {
  const { isAuthenticated, requireAuth, openLoginModal } = useAuth();
  const brand = useBrand();
  const canvasRef = useRef(null);

  // Feature flags: Check if AI editing features are enabled for this brand
  const bgRemovalEnabled = brand.features?.aiEditing?.enableBackgroundRemoval !== false;
  const aiEditEnabled = brand.features?.aiEditing?.enableAiEdit !== false;
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [isCropping, setIsCropping] = useState(false);
  const [cropMode, setCropMode] = useState('rectangle'); // 'rectangle', 'circle', 'oval'
  const [cropStart, setCropStart] = useState(null);
  const [cropEnd, setCropEnd] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [baseImage, setBaseImage] = useState(null); // Stores the truly original image for reset
  const [bgRemoved, setBgRemoved] = useState(false); // Track if background was removed
  const [hasTransparency, setHasTransparency] = useState(false); // Track if image has any transparency
  const [bgRemovedPrintifyId, setBgRemovedPrintifyId] = useState(null); // Printify ID from bg removal
  const [bgRemovedImageUrl, setBgRemovedImageUrl] = useState(null); // Store the bg-removed image URL for direct use
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [canvasTainted, setCanvasTainted] = useState(false);
  const [isSavingDesign, setIsSavingDesign] = useState(false);
  const [designSaved, setDesignSaved] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true); // Track initial image loading

  // AI Edit state
  const [editPrompt, setEditPrompt] = useState('');
  const [isApplyingEdit, setIsApplyingEdit] = useState(false);
  const [originalAutoSaved, setOriginalAutoSaved] = useState(false); // Track if original was auto-saved before AI edit
  const [aiEditApplied, setAiEditApplied] = useState(false); // Track if AI edit was applied (to force new upload)

  // HIGH-RES EXPORT: Store reference to original high-res image and track display scale
  const originalImageRef = useRef(null); // Immutable high-res source for export
  const [displayScale, setDisplayScale] = useState(1); // Scale factor from original to display
  const [normalizedCropBounds, setNormalizedCropBounds] = useState(null); // { x: 0-1, y: 0-1, width: 0-1, height: 0-1 }

  // Helper function to detect if an image has transparency
  const checkImageTransparency = useCallback((img) => {
    try {
      const testCanvas = document.createElement('canvas');
      const size = Math.min(img.width, img.height, 100); // Sample a portion for performance
      testCanvas.width = size;
      testCanvas.height = size;
      const ctx = testCanvas.getContext('2d');
      ctx.drawImage(img, 0, 0, size, size);
      const imageData = ctx.getImageData(0, 0, size, size);
      const data = imageData.data;
      // Check alpha channel (every 4th value starting at index 3)
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 250) { // Allow some tolerance for anti-aliasing
          return true;
        }
      }
      return false;
    } catch (e) {
      // Canvas tainted or other error - assume no transparency
      console.warn('Could not check transparency:', e.message);
      return false;
    }
  }, []);

  /**
   * HIGH-RES EXPORT: Exports the current image at full resolution with all transforms applied.
   * Creates an offscreen canvas at original dimensions, applies rotation/brightness/contrast/crop,
   * and returns a data URL. This preserves print quality unlike the display canvas which is scaled down.
   */
  const exportHighRes = useCallback(async () => {
    // Use the current working image (after bg removal/AI edit) or original high-res
    const sourceImage = originalImage || originalImageRef.current;
    if (!sourceImage) {
      console.error('exportHighRes: No source image available');
      return null;
    }

    console.log('=== exportHighRes START ===');
    console.log('Source image dimensions:', sourceImage.width, 'x', sourceImage.height);
    console.log('Transforms:', { rotation, brightness, contrast, normalizedCropBounds, cropMode });

    // Calculate output dimensions based on rotation
    const isRotated = rotation === 90 || rotation === 270;
    let outputWidth = isRotated ? sourceImage.height : sourceImage.width;
    let outputHeight = isRotated ? sourceImage.width : sourceImage.height;

    // Apply crop bounds if set (normalized coordinates)
    let cropBounds = normalizedCropBounds;
    if (cropBounds) {
      outputWidth = Math.round(outputWidth * cropBounds.width);
      outputHeight = Math.round(outputHeight * cropBounds.height);
    }

    // Cap at EXPORT_MAX_SIZE to prevent memory issues with very large images
    const maxDim = Math.max(outputWidth, outputHeight);
    const exportScale = maxDim > EXPORT_MAX_SIZE ? EXPORT_MAX_SIZE / maxDim : 1;
    if (exportScale < 1) {
      console.log(`Capping export from ${maxDim}px to ${EXPORT_MAX_SIZE}px (scale: ${exportScale.toFixed(3)})`);
    }
    outputWidth = Math.round(outputWidth * exportScale);
    outputHeight = Math.round(outputHeight * exportScale);

    console.log('Output dimensions:', outputWidth, 'x', outputHeight);

    // Create intermediate canvas for rotation + filters at full resolution
    const intermediate = document.createElement('canvas');
    const intWidth = isRotated ? sourceImage.height : sourceImage.width;
    const intHeight = isRotated ? sourceImage.width : sourceImage.height;
    intermediate.width = Math.round(intWidth * exportScale);
    intermediate.height = Math.round(intHeight * exportScale);
    const intCtx = intermediate.getContext('2d');

    // Apply rotation and filters
    intCtx.save();
    intCtx.translate(intermediate.width / 2, intermediate.height / 2);
    intCtx.rotate((rotation * Math.PI) / 180);
    intCtx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
    intCtx.drawImage(
      sourceImage,
      -sourceImage.width * exportScale / 2,
      -sourceImage.height * exportScale / 2,
      sourceImage.width * exportScale,
      sourceImage.height * exportScale
    );
    intCtx.restore();

    // Create final output canvas
    const offscreen = document.createElement('canvas');
    offscreen.width = outputWidth;
    offscreen.height = outputHeight;
    const ctx = offscreen.getContext('2d');

    // Apply crop if set
    if (cropBounds) {
      const sx = Math.round(intermediate.width * cropBounds.x);
      const sy = Math.round(intermediate.height * cropBounds.y);
      const sw = Math.round(intermediate.width * cropBounds.width);
      const sh = Math.round(intermediate.height * cropBounds.height);

      if (cropMode === 'circle' || cropMode === 'oval') {
        // Draw cropped region first
        ctx.drawImage(intermediate, sx, sy, sw, sh, 0, 0, outputWidth, outputHeight);
        // Apply ellipse mask for transparency
        ctx.globalCompositeOperation = 'destination-in';
        ctx.beginPath();
        ctx.ellipse(outputWidth / 2, outputHeight / 2, outputWidth / 2, outputHeight / 2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
      } else {
        // Rectangle crop - simple extraction
        ctx.drawImage(intermediate, sx, sy, sw, sh, 0, 0, outputWidth, outputHeight);
      }
    } else {
      // No crop - draw full intermediate canvas
      ctx.drawImage(intermediate, 0, 0);
    }

    const dataUrl = offscreen.toDataURL('image/png');
    console.log('=== exportHighRes COMPLETE ===');
    console.log('Exported data URL length:', dataUrl.length, 'bytes');

    return dataUrl;
  }, [originalImage, rotation, brightness, contrast, normalizedCropBounds, cropMode]);

  /**
   * Resize an image data URL to fit within maxSize while maintaining aspect ratio.
   * Used to ensure images stay under API size limits for background removal.
   * This resize is silent/invisible to the user.
   */
  const resizeImageForApi = useCallback(async (dataUrl, maxSize = OPTIMAL_BG_REMOVAL_SIZE) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = Math.max(img.width, img.height);

        // If already within limits, return original
        if (maxDim <= maxSize) {
          resolve({ dataUrl, width: img.width, height: img.height, wasResized: false });
          return;
        }

        // Calculate new dimensions maintaining aspect ratio
        const scale = maxSize / maxDim;
        const newWidth = Math.round(img.width * scale);
        const newHeight = Math.round(img.height * scale);

        // Create canvas and resize with high quality
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        resolve({
          dataUrl: canvas.toDataURL('image/png'),
          width: newWidth,
          height: newHeight,
          wasResized: true,
          originalWidth: img.width,
          originalHeight: img.height
        });
      };
      img.onerror = () => reject(new Error('Failed to load image for resizing'));
      img.src = dataUrl;
    });
  }, []);

  /**
   * Validate that image meets minimum print quality requirements.
   * Returns { isValid: true } or { isValid: false, message: '...' }
   */
  const validatePrintQuality = useCallback((width, height) => {
    const maxDim = Math.max(width, height);
    if (maxDim < MIN_PRINT_SIZE) {
      return {
        isValid: false,
        message: `Image resolution too low (${width}x${height}px). Please upload an image at least ${MIN_PRINT_SIZE}px for quality printing.`
      };
    }
    return { isValid: true };
  }, []);

  // Load the image - convert external URLs to data URLs to avoid canvas tainting
  useEffect(() => {
    if (!image?.url) return;

    // Reset AI edit flag when a new image is loaded
    setAiEditApplied(false);

    // fallbackIndex: -1 = primary URL, 0+ = index into urlFallbacks array
    const loadImage = async (urlToLoad, fallbackIndex = -1) => {
      const imageUrl = urlToLoad || image.url;
      setIsImageLoading(true); // Start loading indicator
      setCanvasTainted(false);

      // Check if it's a data URL (base64) - no conversion needed
      const isDataUrl = imageUrl.startsWith('data:');

      // Check if it's a local blob URL - no conversion needed
      const isBlobUrl = imageUrl.startsWith('blob:');

      // Check if this is one of our saved design images (has s3Key)
      // Use dedicated S3 endpoint instead of proxy for better reliability
      const isOurS3Design = image.s3Key && image.s3Key.startsWith('designs/');

      // Check if it's an external URL that needs proxy/conversion
      // This includes external S3 URLs which may have CORS issues
      const isExternalUrl = !isDataUrl && !isBlobUrl && !isOurS3Design && (
        imageUrl.includes('pfy-prod-image-storage.s3') ||
        imageUrl.includes('images-api.printify') ||
        imageUrl.includes('s3.amazonaws.com') ||
        imageUrl.includes('s3.us-east-1.amazonaws.com') ||
        imageUrl.includes('printifyimages') ||
        imageUrl.includes('amazonaws.com') ||
        imageUrl.includes('d38hhp6342myl9.cloudfront.net')  // World Illustrated CDN
      );

      // For our saved designs, use the dedicated S3 image endpoint (bypasses CORS issues)
      if (isOurS3Design) {
        try {
          const apiEndpoint = getApiEndpoint();
          const fetchUrl = `${apiEndpoint}/api/designs/image/${encodeURIComponent(image.s3Key)}`;

          console.log('Fetching saved design from S3:', image.s3Key);

          const response = await fetch(fetchUrl);

          if (response.status === 413) {
            // Image too large for Lambda response - try fetch API to download as blob
            // This bypasses <img crossOrigin> restrictions that browsers cache aggressively
            console.warn('Design image too large for Lambda, trying fetch API for S3 access');

            try {
              // Use fetch API with mode: 'cors' to download the image
              const s3Response = await fetch(imageUrl, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-store' // Don't use cached CORS failures
              });

              if (!s3Response.ok) {
                throw new Error(`S3 fetch failed: ${s3Response.status}`);
              }

              const blob = await s3Response.blob();
              const blobUrl = URL.createObjectURL(blob);

              console.log('Successfully fetched large design via fetch API, blob size:', (blob.size / 1024 / 1024).toFixed(2), 'MB');

              const img = new Image();
              img.onload = () => {
                setOriginalImage(img);
                setBaseImage(img);
                originalImageRef.current = img;
                setCanvasTainted(false); // Blob URLs are always same-origin
                setHasTransparency(checkImageTransparency(img));
                setIsImageLoading(false);
                console.log('Large design loaded via fetch API - all editing features available, dimensions:', img.width, 'x', img.height);
              };
              img.onerror = () => {
                console.error('Failed to load blob URL');
                URL.revokeObjectURL(blobUrl);
                setIsImageLoading(false);
              };
              img.src = blobUrl;
              return;
            } catch (fetchErr) {
              console.warn('Fetch API for S3 failed:', fetchErr.message, '- falling back to img tag');

              // Fallback: try img tag with crossOrigin
              const img = new Image();
              img.crossOrigin = 'anonymous';

              img.onload = () => {
                setOriginalImage(img);
                setBaseImage(img);
                originalImageRef.current = img;

                // Test if canvas is tainted
                const testCanvas = document.createElement('canvas');
                testCanvas.width = 1;
                testCanvas.height = 1;
                const ctx = testCanvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                try {
                  testCanvas.toDataURL();
                  setCanvasTainted(false);
                  setHasTransparency(checkImageTransparency(img));
                  console.log('Large design loaded directly from S3 with CORS - editing available, dimensions:', img.width, 'x', img.height);
                } catch (e) {
                  console.warn('Canvas tainted despite CORS - editing limited');
                  setCanvasTainted(true);
                }
                setIsImageLoading(false);
              };

              img.onerror = () => {
                console.warn('Direct S3 CORS load failed, loading without CORS (editing will be limited)');
                // Fallback: load without crossOrigin
                setCanvasTainted(true);
                const img2 = new Image();
                img2.onload = () => {
                  setOriginalImage(img2);
                  setBaseImage(img2);
                  console.log('Large design loaded without CORS - editing limited');
                  setIsImageLoading(false);
                };
                img2.onerror = () => {
                  console.error('Failed to load large design image');
                  setIsImageLoading(false);
                };
                img2.src = imageUrl;
              };

              img.src = imageUrl;
            }
            return;
          }

          if (!response.ok) {
            throw new Error(`Failed to fetch design image: ${response.status}`);
          }

          const result = await response.json();

          if (!result.success || !result.dataUrl) {
            throw new Error(result.error || 'S3 endpoint did not return data URL');
          }

          console.log('Received design image from S3, size:', result.sizeMB, 'MB');

          // Load the data URL into an Image object
          const img = new Image();
          img.onload = () => {
            setOriginalImage(img);
            setBaseImage(img);
            originalImageRef.current = img;
            setCanvasTainted(false);
            setHasTransparency(checkImageTransparency(img));
            setIsImageLoading(false);
            console.log('Saved design loaded from S3 - all editing features available, dimensions:', img.width, 'x', img.height);
          };
          img.onerror = (e) => {
            console.error('Failed to load design data URL:', e);
            loadDirectly(imageUrl, false);
          };
          img.src = result.dataUrl;
          return;
        } catch (err) {
          console.warn('Failed to fetch design from S3 endpoint:', err.message);
          // Fall through to external URL handling
        }
      }

      // For external URLs that might cause CORS issues, fetch via proxy as data URL
      if (isExternalUrl) {
        // Use proxy for all external URLs
        // Direct CORS access with presigned URLs has issues, proxy is more reliable
        try {
          const apiEndpoint = getApiEndpoint();
          // Use dataurl=true to get base64 data URL directly from server (avoids API Gateway binary issues)
          // Add cache buster to avoid stale cached responses
          const cacheBuster = Date.now();
          const fetchUrl = `${apiEndpoint}/api/images/proxy?dataurl=true&_=${cacheBuster}&url=${encodeURIComponent(imageUrl)}`;

          console.log('Fetching external image via proxy:', imageUrl);

          const response = await fetch(fetchUrl);

          // If proxy returns 413 (too large), try direct load with CORS
          if (response.status === 413) {
            console.warn('Image too large for proxy, trying direct load with CORS');
            loadDirectly(imageUrl, false);
            return;
          }

          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status}`);
          }

          const result = await response.json();

          if (!result.success || !result.dataUrl) {
            throw new Error(result.error || 'Proxy did not return data URL');
          }

          console.log('Received data URL from proxy, length:', result.dataUrl.length);

          // Load the data URL into an Image object
          const img = new Image();
          img.onload = () => {
            setOriginalImage(img);
            setBaseImage(img); // Store as base image for reset
            originalImageRef.current = img; // Store high-res reference for export
            setCanvasTainted(false);
            setHasTransparency(checkImageTransparency(img));
            setIsImageLoading(false); // Image loaded successfully
            console.log('External image loaded as data URL - all editing features available, dimensions:', img.width, 'x', img.height);
          };
          img.onerror = (e) => {
            console.error('Failed to load data URL from proxy:', e);
            // Try next fallback URL if available
            const fallbacks = image.urlFallbacks || [];
            const nextFallbackIndex = fallbackIndex + 1;
            if (nextFallbackIndex < fallbacks.length) {
              console.log(`Trying fallback URL #${nextFallbackIndex + 1}:`, fallbacks[nextFallbackIndex]);
              loadImage(fallbacks[nextFallbackIndex], nextFallbackIndex);
              return;
            }
            // Fall back to direct load (will set isImageLoading to false)
            loadDirectly(imageUrl, false, fallbackIndex);
          };
          img.src = result.dataUrl;
          return;
        } catch (err) {
          console.warn('Failed to get image via proxy:', err.message);
          // Try next fallback URL if available
          const fallbacks = image.urlFallbacks || [];
          const nextFallbackIndex = fallbackIndex + 1;
          if (nextFallbackIndex < fallbacks.length) {
            console.log(`Proxy failed, trying fallback URL #${nextFallbackIndex + 1}:`, fallbacks[nextFallbackIndex]);
            loadImage(fallbacks[nextFallbackIndex], nextFallbackIndex);
            return;
          }
          // Fall through to direct load (will handle isImageLoading)
        }
      }

      // Direct load for data URLs, blob URLs, local URLs, or if conversion failed
      loadDirectly(imageUrl, isDataUrl || isBlobUrl, fallbackIndex);
    };

    // Helper function for direct image loading
    // fallbackIndex: -1 = primary URL, 0+ = index into urlFallbacks array
    const loadDirectly = (url, isSafeUrl, fallbackIndex = -1) => {
      const img = new Image();

      // Check if this is a presigned S3 URL (has X-Amz-Signature or similar)
      // Presigned URLs cannot have extra parameters added - it invalidates the signature
      const isPresignedUrl = url.includes('X-Amz-') || url.includes('x-amz-');

      // For cross-origin URLs (not presigned), add cache buster to avoid 304 conflicts with crossOrigin
      let loadUrl = url;
      if (!isSafeUrl && !isPresignedUrl) {
        if (!url.includes('?')) {
          loadUrl = `${url}?_cors=${Date.now()}`;
        } else {
          loadUrl = `${url}&_cors=${Date.now()}`;
        }
      }

      // Only set crossOrigin for potentially cross-origin URLs
      if (!isSafeUrl) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = () => {
        setOriginalImage(img);
        setBaseImage(img); // Store as base image for reset
        originalImageRef.current = img; // Store high-res reference for export
        // Check if canvas will be tainted by trying a test
        if (!isSafeUrl) {
          const testCanvas = document.createElement('canvas');
          testCanvas.width = 1;
          testCanvas.height = 1;
          const ctx = testCanvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          try {
            testCanvas.toDataURL();
            setCanvasTainted(false);
            setHasTransparency(checkImageTransparency(img));
            console.log('Image loaded directly with CORS - editing available, dimensions:', img.width, 'x', img.height);
          } catch (e) {
            console.warn('Canvas tainted - some editing features limited');
            setCanvasTainted(true);
          }
        } else {
          setCanvasTainted(false);
          setHasTransparency(checkImageTransparency(img));
          console.log('Safe URL loaded - all editing features available, dimensions:', img.width, 'x', img.height);
        }
        setIsImageLoading(false); // Image loaded successfully
      };

      img.onerror = () => {
        console.error('Failed to load image with crossOrigin:', url);

        // Try the next fallback URL if available (e.g., Rawpixel 2000px -> 1000px -> 800px)
        const fallbacks = image.urlFallbacks || [];
        const nextFallbackIndex = fallbackIndex + 1;
        if (nextFallbackIndex < fallbacks.length) {
          console.log(`Trying fallback URL #${nextFallbackIndex + 1}:`, fallbacks[nextFallbackIndex]);
          loadDirectly(fallbacks[nextFallbackIndex], false, nextFallbackIndex);
          return;
        }

        // Fallback: try loading without crossOrigin (won't be editable but will display)
        if (!isSafeUrl) {
          console.log('All fallbacks exhausted, trying to load without CORS...');
          setCanvasTainted(true);
          const img2 = new Image();
          img2.onload = () => {
            setOriginalImage(img2);
            setBaseImage(img2); // Store as base image for reset
            // Can't check transparency on tainted canvas
            console.log('Image loaded without CORS - editing limited');
            setIsImageLoading(false); // Image loaded (with limitations)
          };
          img2.onerror = () => {
            console.error('Failed to load image completely');
            setIsImageLoading(false); // Stop loading on complete failure
          };
          // Add cache buster for non-presigned URLs to avoid cached CORS failure
          // For presigned URLs, use as-is since adding params invalidates signature
          let noCorsUrl = url;
          if (!isPresignedUrl && !url.includes('_nocors=')) {
            noCorsUrl = url.includes('?') ? `${url}&_nocors=${Date.now()}` : `${url}?_nocors=${Date.now()}`;
          }
          img2.src = noCorsUrl;
        } else {
          console.error('Failed to load data/blob URL');
          setIsImageLoading(false); // Stop loading on failure
        }
      };

      img.src = loadUrl;
    };

    loadImage(image.url, -1);  // Start with primary URL (fallbackIndex = -1)
  }, [image?.url, image?.urlFallbacks]);

  // Render the canvas whenever settings change
  useEffect(() => {
    if (!originalImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Calculate dimensions based on rotation
    const isRotated = rotation === 90 || rotation === 270;
    const displayWidth = isRotated ? originalImage.height : originalImage.width;
    const displayHeight = isRotated ? originalImage.width : originalImage.height;

    // Scale to fit container (max DISPLAY_MAX_SIZE for preview)
    const scale = Math.min(DISPLAY_MAX_SIZE / displayWidth, DISPLAY_MAX_SIZE / displayHeight, 1);
    setDisplayScale(scale); // Track scale for high-res export coordinate conversion
    canvas.width = displayWidth * scale;
    canvas.height = displayHeight * scale;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw checkerboard pattern when image has transparency (to show transparent areas)
    if (bgRemoved || hasTransparency) {
      const squareSize = 10;
      for (let row = 0; row < canvas.height / squareSize; row++) {
        for (let col = 0; col < canvas.width / squareSize; col++) {
          ctx.fillStyle = (row + col) % 2 === 0 ? '#f0f0f0' : '#d0d0d0';
          ctx.fillRect(col * squareSize, row * squareSize, squareSize, squareSize);
        }
      }
    }

    ctx.save();

    // Move to center, rotate, then draw
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);

    // Apply filters
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;

    // Draw image centered
    const drawWidth = originalImage.width * scale;
    const drawHeight = originalImage.height * scale;
    ctx.drawImage(originalImage, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);

    ctx.restore();

    // Check if canvas is actually tainted after drawing
    try {
      canvas.toDataURL();
      // If we get here, canvas is not tainted
      if (canvasTainted) {
        setCanvasTainted(false);
      }
    } catch (e) {
      // Canvas is tainted
      if (!canvasTainted) {
        console.warn('Canvas became tainted after drawing image');
        setCanvasTainted(true);
      }
    }

    // Draw crop overlay if cropping
    if (isCropping && cropStart && cropEnd) {
      // Light semi-transparent overlay so you can still see the image
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const x = Math.min(cropStart.x, cropEnd.x);
      const y = Math.min(cropStart.y, cropEnd.y);
      const w = Math.abs(cropEnd.x - cropStart.x);
      const h = Math.abs(cropEnd.y - cropStart.y);

      // Calculate center and radii for circle/oval modes
      const centerX = x + w / 2;
      const centerY = y + h / 2;
      const radiusX = w / 2;
      const radiusY = cropMode === 'circle' ? Math.min(w, h) / 2 : h / 2;

      // Clear the selected area based on crop mode
      ctx.save();
      ctx.beginPath();
      if (cropMode === 'rectangle') {
        ctx.rect(x, y, w, h);
      } else {
        // Circle or oval - use ellipse
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
      }
      ctx.clip();

      // Clear and redraw the clipped area
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Redraw checkerboard for transparent images
      if (bgRemoved || hasTransparency) {
        const squareSize = 10;
        for (let row = 0; row < canvas.height / squareSize; row++) {
          for (let col = 0; col < canvas.width / squareSize; col++) {
            ctx.fillStyle = (row + col) % 2 === 0 ? '#f0f0f0' : '#d0d0d0';
            ctx.fillRect(col * squareSize, row * squareSize, squareSize, squareSize);
          }
        }
      }

      // Redraw just the selected portion of the image (without overlay)
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
      const isRotated2 = rotation === 90 || rotation === 270;
      const displayWidth2 = isRotated2 ? originalImage.height : originalImage.width;
      const displayHeight2 = isRotated2 ? originalImage.width : originalImage.height;
      const scale2 = Math.min(DISPLAY_MAX_SIZE / displayWidth2, DISPLAY_MAX_SIZE / displayHeight2, 1);
      const drawWidth2 = originalImage.width * scale2;
      const drawHeight2 = originalImage.height * scale2;
      ctx.drawImage(originalImage, -drawWidth2 / 2, -drawHeight2 / 2, drawWidth2, drawHeight2);
      ctx.restore();

      // Draw border around selected area
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      if (cropMode === 'rectangle') {
        ctx.strokeRect(x, y, w, h);
      } else {
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
        ctx.stroke();
      }
    }

    // Show applied crop bounds (after crop is confirmed but before export)
    if (normalizedCropBounds && !isCropping) {
      // Convert normalized bounds back to display coordinates
      const cropX = normalizedCropBounds.x * canvas.width;
      const cropY = normalizedCropBounds.y * canvas.height;
      const cropW = normalizedCropBounds.width * canvas.width;
      const cropH = normalizedCropBounds.height * canvas.height;

      // Dim area outside crop
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      // Top
      ctx.fillRect(0, 0, canvas.width, cropY);
      // Bottom
      ctx.fillRect(0, cropY + cropH, canvas.width, canvas.height - cropY - cropH);
      // Left
      ctx.fillRect(0, cropY, cropX, cropH);
      // Right
      ctx.fillRect(cropX + cropW, cropY, canvas.width - cropX - cropW, cropH);

      // Draw border around crop area
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      if (cropMode === 'circle' || cropMode === 'oval') {
        ctx.beginPath();
        ctx.ellipse(cropX + cropW / 2, cropY + cropH / 2, cropW / 2, cropH / 2, 0, 0, 2 * Math.PI);
        ctx.stroke();
      } else {
        ctx.strokeRect(cropX, cropY, cropW, cropH);
      }

      // Label
      ctx.fillStyle = '#10b981';
      ctx.font = '12px sans-serif';
      ctx.fillText('Crop applied', cropX + 4, cropY - 4);
    }
  }, [originalImage, rotation, brightness, contrast, isCropping, cropMode, cropStart, cropEnd, bgRemoved, hasTransparency, normalizedCropBounds]);

  const handleRotate = (direction) => {
    setRotation((prev) => (prev + direction + 360) % 360);
  };

  const handleReset = () => {
    setRotation(0);
    setBrightness(100);
    setContrast(100);
    setIsCropping(false);
    setCropStart(null);
    setCropEnd(null);
    setNormalizedCropBounds(null); // Clear high-res crop bounds
    setBgRemoved(false);
    setBgRemovedPrintifyId(null);
    setBgRemovedImageUrl(null);
    setAiEditApplied(false); // Reset AI edit flag
    // Restore the original image if we have it (undoes cropping and background removal)
    if (baseImage) {
      setOriginalImage(baseImage);
      originalImageRef.current = baseImage; // Restore high-res reference
      // Re-check transparency on original image
      setHasTransparency(checkImageTransparency(baseImage));
    }
  };

  const handleCropStart = (e) => {
    if (!isCropping) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setCropStart({ x, y });
    setCropEnd({ x, y });
  };

  const handleCropMove = (e) => {
    if (!isCropping || !cropStart) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    setCropEnd({ x, y });
  };

  const handleCropEnd = () => {
    if (!isCropping || !cropStart || !cropEnd) return;

    const canvas = canvasRef.current;
    const x = Math.min(cropStart.x, cropEnd.x);
    const y = Math.min(cropStart.y, cropEnd.y);
    const w = Math.abs(cropEnd.x - cropStart.x);
    const h = Math.abs(cropEnd.y - cropStart.y);

    if (w > 10 && h > 10 && originalImage) {
      // Check if canvas is tainted before trying to crop
      if (canvasTainted) {
        alert('Cropping is not available for this image due to browser security restrictions. Try uploading the image directly instead of using a saved design.');
        setIsCropping(false);
        setCropStart(null);
        setCropEnd(null);
        return;
      }

      // For circle mode, use the smaller dimension for both width and height
      const centerX = x + w / 2;
      const centerY = y + h / 2;
      const finalW = cropMode === 'circle' ? Math.min(w, h) : w;
      const finalH = cropMode === 'circle' ? Math.min(w, h) : h;
      const finalX = cropMode === 'circle' ? centerX - finalW / 2 : x;
      const finalY = cropMode === 'circle' ? centerY - finalH / 2 : y;

      // HIGH-RES CROP: Store normalized coordinates (0-1 range) instead of pixel values
      // This allows the crop to be applied at full resolution during export
      const normalizedBounds = {
        x: finalX / canvas.width,
        y: finalY / canvas.height,
        width: finalW / canvas.width,
        height: finalH / canvas.height
      };

      console.log('Crop bounds (normalized):', normalizedBounds);
      console.log('Canvas size:', canvas.width, 'x', canvas.height);
      console.log('Original image size:', originalImage.width, 'x', originalImage.height);

      setNormalizedCropBounds(normalizedBounds);

      // Circle/oval crop will create transparency when exported
      if (cropMode !== 'rectangle') {
        setHasTransparency(true);
      }
    }

    setIsCropping(false);
    setCropStart(null);
    setCropEnd(null);
  };

  const handleRemoveBackground = async () => {
    if (!onRemoveBackground) return;

    // SECURITY: Require login for AI background removal
    if (!isAuthenticated) {
      openLoginModal('Sign in to use AI background removal');
      return;
    }

    // Validate minimum print quality before processing
    const sourceImage = originalImage || originalImageRef.current;
    if (sourceImage) {
      const qualityCheck = validatePrintQuality(sourceImage.width, sourceImage.height);
      if (!qualityCheck.isValid) {
        alert(qualityCheck.message);
        return;
      }
    }

    setIsRemovingBg(true);
    try {
      let imageData;

      // HIGH-RES: Export current state at full resolution before sending to background removal API
      // This ensures the API receives the highest quality image with any transforms applied
      if (canvasTainted) {
        console.log('Canvas is tainted, using original image URL for background removal');
        imageData = image.originalUrl || image.url;
      } else {
        try {
          console.log('Exporting high-res image for background removal...');
          imageData = await exportHighRes();
          if (!imageData) {
            throw new Error('High-res export returned null');
          }
          console.log('High-res export successful for background removal, length:', imageData.length);

          // Silently resize if image exceeds API size limits (invisible to user)
          // Use smart calculation to resize in ONE pass based on file size, not just dimensions
          if (sourceImage) {
            const currentSizeMB = (imageData.length * 0.75) / (1024 * 1024);
            const TARGET_SIZE_MB = 5; // Safe margin under API Gateway 6MB limit

            if (currentSizeMB > TARGET_SIZE_MB) {
              console.log(`Image ${currentSizeMB.toFixed(1)}MB exceeds target ${TARGET_SIZE_MB}MB, calculating optimal resize...`);

              // Calculate optimal dimensions based on size ratio
              // File size scales with pixel area (width × height), so scale by sqrt
              const sizeRatio = TARGET_SIZE_MB / currentSizeMB;
              const dimensionScale = Math.sqrt(sizeRatio) * 0.9; // 0.9 = safety margin for compression variance

              const currentMaxDim = Math.max(sourceImage.width, sourceImage.height);
              const targetMaxDim = Math.round(currentMaxDim * dimensionScale);

              console.log(`Smart resize: ${currentMaxDim}px → ${targetMaxDim}px (scale: ${dimensionScale.toFixed(2)})`);

              const resizeResult = await resizeImageForApi(imageData, targetMaxDim);
              imageData = resizeResult.dataUrl;

              if (resizeResult.wasResized) {
                const finalSizeMB = (imageData.length * 0.75) / (1024 * 1024);
                console.log(`Resized: ${resizeResult.originalWidth}x${resizeResult.originalHeight} → ${resizeResult.width}x${resizeResult.height}`);
                console.log(`Final size: ${finalSizeMB.toFixed(1)}MB (target: ${TARGET_SIZE_MB}MB)`);
              }
            }
          }
        } catch (err) {
          console.warn('High-res export failed, trying canvas fallback:', err.message);
          try {
            const canvas = canvasRef.current;
            imageData = canvas.toDataURL('image/png');
          } catch (canvasErr) {
            console.warn('Canvas toDataURL failed, using original image URL:', canvasErr.message);
            imageData = image.originalUrl || image.url;
          }
        }
      }

      console.log('Sending to background removal, imageData type:',
        imageData?.startsWith('data:') ? 'data URL' : 'external URL',
        'length:', imageData?.length);

      // Call the AI background removal
      const result = await onRemoveBackground(imageData);

      console.log('Background removal result:', {
        hasUrl: !!result?.url,
        printifyImageId: result?.printifyImageId,
        urlStart: result?.url?.substring(0, 50)
      });

      // Handle auth/rate limit errors
      if (result?.error === 'AUTH_REQUIRED') {
        openLoginModal('Please sign in to use AI background removal');
        setIsRemovingBg(false);
        return;
      }

      if (result?.error === 'RATE_LIMITED') {
        alert(result.message || 'Rate limit reached. Please try again later.');
        setIsRemovingBg(false);
        return;
      }

      // Handle image too large error (shouldn't happen after frontend resize, but just in case)
      if (result?.error === 'IMAGE_TOO_LARGE') {
        console.error('Image still too large after resize:', result.details);
        alert(result.message || 'Image is too large. Please try with a smaller image.');
        setIsRemovingBg(false);
        return;
      }

      if (result?.url) {
        const resultUrl = result.url;
        const resultPrintifyId = result.printifyImageId;

        // Check if URL needs to be proxied (external Printify/S3 URLs)
        const needsProxy = !resultUrl.startsWith('data:') && (
          resultUrl.includes('pfy-prod-image-storage.s3') ||
          resultUrl.includes('images-api.printify') ||
          resultUrl.includes('s3.amazonaws.com') ||
          resultUrl.includes('amazonaws.com')
        );

        if (needsProxy) {
          // Fetch via proxy to avoid CORS issues
          console.log('Background removed image needs proxy:', resultUrl);
          try {
            const apiEndpoint = getApiEndpoint();
            const cacheBuster = Date.now();
            const fetchUrl = `${apiEndpoint}/api/images/proxy?dataurl=true&_=${cacheBuster}&url=${encodeURIComponent(resultUrl)}`;

            const response = await fetch(fetchUrl);
            if (!response.ok) throw new Error(`Proxy fetch failed: ${response.status}`);

            const proxyResult = await response.json();
            if (!proxyResult.success || !proxyResult.dataUrl) {
              throw new Error(proxyResult.error || 'Proxy did not return data URL');
            }

            const img = new Image();
            img.onload = () => {
              console.log('=== BG Removal Complete (proxied) ===');
              console.log('Setting bgRemovedPrintifyId to:', resultPrintifyId);
              console.log('Image dimensions:', img.width, 'x', img.height);
              setOriginalImage(img);
              originalImageRef.current = img; // Update high-res reference for future exports
              // Reset crop bounds since transforms are now baked into the bg-removed image
              setNormalizedCropBounds(null);
              setRotation(0);
              setBrightness(100);
              setContrast(100);
              setBgRemoved(true);
              setHasTransparency(true); // Background removal creates transparency
              setBgRemovedPrintifyId(resultPrintifyId || null);
              // Store the data URL so we can use it directly without canvas re-export
              setBgRemovedImageUrl(proxyResult.dataUrl);
              setCanvasTainted(false);
              setIsRemovingBg(false); // Only hide spinner after image is fully loaded
            };
            img.onerror = () => {
              console.error('Failed to load proxied background-removed image');
              alert('Failed to load the background-removed image. Please try again.');
              setIsRemovingBg(false);
            };
            img.src = proxyResult.dataUrl;
          } catch (proxyErr) {
            console.error('Failed to proxy background-removed image:', proxyErr);
            alert('Failed to load the background-removed image. Please try again.');
            setIsRemovingBg(false);
          }
        } else {
          // Data URL or safe URL - load directly
          const img = new Image();
          img.onload = () => {
            console.log('=== BG Removal Complete (direct) ===');
            console.log('Setting bgRemovedPrintifyId to:', resultPrintifyId);
            console.log('Image dimensions:', img.width, 'x', img.height);
            setOriginalImage(img);
            originalImageRef.current = img; // Update high-res reference for future exports
            // Reset crop bounds since transforms are now baked into the bg-removed image
            setNormalizedCropBounds(null);
            setRotation(0);
            setBrightness(100);
            setContrast(100);
            setBgRemoved(true);
            setHasTransparency(true); // Background removal creates transparency
            setBgRemovedPrintifyId(resultPrintifyId || null);
            // Store the URL so we can use it directly without canvas re-export
            setBgRemovedImageUrl(resultUrl);
            setCanvasTainted(false);
            setIsRemovingBg(false); // Only hide spinner after image is fully loaded
          };
          img.onerror = () => {
            console.error('Failed to load background-removed image');
            alert('Failed to load the background-removed image. Please try again.');
            setIsRemovingBg(false);
          };
          img.src = resultUrl;
        }
      } else if (result?.error) {
        console.error('Background removal returned error:', result.error);
        alert(`Background removal failed: ${result.error}`);
        setIsRemovingBg(false);
      } else if (!result) {
        // Result is null/undefined - the parent component already handled the error
        console.log('Background removal returned null (error already handled by parent)');
        setIsRemovingBg(false);
      } else {
        // Result exists but has no URL - unexpected state
        console.error('Background removal returned result without URL:', result);
        alert('Background removal failed. Please try again.');
        setIsRemovingBg(false);
      }
    } catch (error) {
      console.error('Background removal failed:', error);
      alert('Background removal failed. Please try again.');
      setIsRemovingBg(false);
    }
    // Note: We don't use finally block here because setIsRemovingBg(false)
    // needs to happen AFTER the image loads, not immediately after the try block
  };

  const handleSave = async () => {
    if (!canvasRef.current) return;

    // Check if user actually made edits (including cropping, bg removal, transforms, AI edit)
    const hasTransformEdits = rotation !== 0 || brightness !== 100 || contrast !== 100;
    const hasCropEdits = normalizedCropBounds !== null;
    const hasEdits = hasTransformEdits || hasCropEdits || bgRemoved || aiEditApplied;

    // If ONLY background was removed (no cropping or transforms), use the printifyImageId from bg removal
    // This avoids re-uploading and potentially losing transparency
    const onlyBgRemoved = bgRemoved && !hasTransformEdits && !hasCropEdits;
    const finalPrintifyId = onlyBgRemoved && bgRemovedPrintifyId
      ? bgRemovedPrintifyId
      : (hasEdits ? null : image?.printifyImageId);

    try {
      let imageUrl;

      // If ONLY bg was removed and we have the stored bg-removed URL, use it directly
      // This preserves transparency better than re-exporting through canvas
      if (onlyBgRemoved && bgRemovedImageUrl) {
        imageUrl = bgRemovedImageUrl;
        console.log('=== ImageEditor.handleSave (using stored bg-removed URL) ===');
      } else {
        // HIGH-RES EXPORT: Use exportHighRes() for full resolution output
        console.log('=== ImageEditor.handleSave (using HIGH-RES export) ===');
        imageUrl = await exportHighRes();

        if (!imageUrl) {
          console.error('High-res export failed, falling back to canvas export');
          imageUrl = canvasRef.current.toDataURL('image/png');
        }
      }

      console.log('handleSave state:', {
        bgRemoved,
        hasTransformEdits,
        hasCropEdits,
        onlyBgRemoved,
        finalPrintifyId,
        imageUrlLength: imageUrl?.length
      });

      onSave?.({
        ...image,
        url: imageUrl,
        edited: hasEdits,
        // Use bg removal printifyId if only bg was removed, otherwise clear if edits were made
        printifyImageId: finalPrintifyId,
        // Include original dimensions for reference
        originalWidth: originalImageRef.current?.width,
        originalHeight: originalImageRef.current?.height,
        // Preserve fromSaved flag so DesignWorkflow can force re-upload for saved designs
        fromSaved: image?.fromSaved || false
      });
    } catch (err) {
      // Canvas is tainted - can't export, just pass original image with visual changes noted
      console.warn('Export failed, saving original image reference:', err);
      onSave?.({
        ...image,
        edited: true,
        editedSettings: { rotation, brightness, contrast },
        // Keep printifyImageId since we couldn't export the edits
        printifyImageId: image?.printifyImageId,
        // Preserve fromSaved flag so DesignWorkflow can force re-upload for saved designs
        fromSaved: image?.fromSaved || false
      });
    }
  };

  // Download the current design as PNG at high resolution
  const handleDownload = async () => {
    if (!canvasRef.current) return;

    try {
      // HIGH-RES EXPORT: Use exportHighRes() for full resolution download
      console.log('Downloading at high resolution...');
      const dataUrl = await exportHighRes();

      if (!dataUrl) {
        throw new Error('High-res export returned null');
      }

      const link = document.createElement('a');
      link.download = `design-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      console.log('Download initiated with high-res image');
    } catch (err) {
      console.warn('High-res export failed, trying canvas fallback:', err);
      try {
        const dataUrl = canvasRef.current.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `design-${Date.now()}.png`;
        link.href = dataUrl;
        link.click();
      } catch (canvasErr) {
        console.warn('Canvas tainted, cannot download edited image');
        // Fallback: try to download original image
        if (image?.url && !image.url.startsWith('data:')) {
          window.open(image.url, '_blank');
        } else {
          alert('Unable to download image due to browser restrictions.');
        }
      }
    }
  };

  // Save design to localStorage (and optionally to backend)
  const handleSaveDesign = async () => {
    if (!canvasRef.current) return;

    setIsSavingDesign(true);
    try {
      // HIGH-RES EXPORT: Use exportHighRes() for full resolution saved designs
      let designData;
      try {
        console.log('Saving design at high resolution...');
        designData = await exportHighRes();
        if (!designData) {
          throw new Error('High-res export returned null');
        }
        console.log('High-res design export successful, length:', designData.length);
      } catch (err) {
        console.warn('High-res export failed, trying canvas fallback:', err);
        try {
          designData = canvasRef.current.toDataURL('image/png');
        } catch (canvasErr) {
          // Canvas tainted - use original URL
          designData = image?.url;
        }
      }

      // Check if user made any edits
      const hasTransformEdits = rotation !== 0 || brightness !== 100 || contrast !== 100;
      const hasCropEdits = normalizedCropBounds !== null;

      let designUrl = designData;
      let s3Key = null;

      // Always upload to S3 for authenticated users - simpler and more reliable
      // S3 keys are stored in DynamoDB, presigned URLs regenerated on fetch
      if (isAuthenticated && designData?.startsWith('data:')) {
        console.log('Uploading high-res design to S3...');
        try {
          const uploadResponse = await amplifyApi.uploadDesignImage(designData);
          if (uploadResponse.success && uploadResponse.url) {
            console.log('Successfully uploaded high-res design to S3:', uploadResponse.s3Key);
            designUrl = uploadResponse.url;  // Presigned URL for immediate display
            s3Key = uploadResponse.s3Key;    // S3 key to store in DynamoDB
          } else {
            console.warn('S3 upload failed:', uploadResponse.error);
            // Fall through to try direct save (may fail for very large images)
          }
        } catch (uploadErr) {
          console.warn('Design image upload failed, trying direct save:', uploadErr);
          // Fall through to try direct save (may fail for very large images)
        }
      }

      // Determine printifyImageId to save:
      // - If any edits were made (transforms, crop), clear printifyImageId
      // - If ONLY bg was removed, use the bgRemovedPrintifyId
      // - Otherwise keep original if no edits at all
      let finalPrintifyId = null;
      if (hasTransformEdits || hasCropEdits) {
        // Transforms or crop invalidate any printify ID
        finalPrintifyId = null;
      } else if (bgRemoved && bgRemovedPrintifyId) {
        // BG was removed, use the new printify ID from bg removal
        finalPrintifyId = bgRemovedPrintifyId;
      } else if (!bgRemoved) {
        // No edits at all, keep original
        finalPrintifyId = image?.printifyImageId || null;
      }

      console.log('handleSaveDesign state:', {
        hasTransformEdits,
        hasCropEdits,
        bgRemoved,
        finalPrintifyId
      });

      const savedDesign = {
        id: `design-${Date.now()}`,
        url: designUrl,  // Presigned URL for display
        s3Key: s3Key,    // S3 key to store in DynamoDB (for regenerating URLs)
        name: image?.name || `Design ${new Date().toLocaleDateString()}`,
        createdAt: new Date().toISOString(),
        settings: { rotation, brightness, contrast },
        originalUrl: image?.url,
        // Use the calculated printifyImageId
        printifyImageId: finalPrintifyId,
        // Preserve AI generation prompt for reference/search
        aiPrompt: image?.aiPrompt || null,
        source: image?.source || image?.imageSource || null
      };

      // Save to cloud if authenticated
      if (isAuthenticated) {
        const response = await amplifyApi.saveDesign(savedDesign);
        if (!response.success) {
          console.error('Failed to save design to cloud:', response.error);
          throw new Error(response.error || 'Failed to save design');
        }
      } else {
        // Fallback to localStorage for non-authenticated users (shouldn't happen with requireAuth)
        const savedDesigns = JSON.parse(localStorage.getItem('savedDesigns') || '[]');
        savedDesigns.unshift(savedDesign);
        if (savedDesigns.length > 20) savedDesigns.pop();
        localStorage.setItem('savedDesigns', JSON.stringify(savedDesigns));
      }

      // If backend save handler provided, call it
      if (onSaveDesign) {
        await onSaveDesign(savedDesign);
      }

      setDesignSaved(true);
      setTimeout(() => setDesignSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save design:', error);
      alert('Failed to save design. Please try again.');
    } finally {
      setIsSavingDesign(false);
    }
  };

  // Handle AI edit with auto-save of original for AI-generated images
  const handleApplyEdit = async () => {
    if (!editPrompt.trim() || isApplyingEdit) return;

    // SECURITY: Require login for AI editing
    if (!isAuthenticated) {
      openLoginModal('Sign in to use AI image editing');
      return;
    }

    setIsApplyingEdit(true);
    try {
      // AUTO-SAVE ORIGINAL: Before applying AI edit, save original to Saved Designs
      // Only for AI-generated images that haven't been saved yet
      if (isAuthenticated && image?.source === 'ai-generated' && !originalAutoSaved && baseImage) {
        try {
          // Get baseImage as data URL for saving
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = baseImage.width;
          tempCanvas.height = baseImage.height;
          tempCanvas.getContext('2d').drawImage(baseImage, 0, 0);
          const originalDataUrl = tempCanvas.toDataURL('image/png');

          // Upload to S3 first
          const uploadResponse = await amplifyApi.uploadDesignImage(originalDataUrl);

          const savedDesign = {
            id: `design-${Date.now()}`,
            url: uploadResponse.success ? uploadResponse.url : originalDataUrl,
            s3Key: uploadResponse.success ? uploadResponse.s3Key : null,
            name: `AI Original - ${new Date().toLocaleDateString()}`,
            createdAt: new Date().toISOString(),
            source: 'ai-generated-auto-save',
            printifyImageId: image?.printifyImageId || null,
            aiPrompt: image?.aiPrompt || null  // Preserve the original generation prompt
          };

          await amplifyApi.saveDesign(savedDesign);
          setOriginalAutoSaved(true);
          console.log('Auto-saved original AI image before edit');
        } catch (saveErr) {
          console.warn('Failed to auto-save original, continuing with edit:', saveErr);
          // Don't block the edit if auto-save fails
        }
      }

      // Get current canvas as base64
      let imageData;
      if (canvasTainted) {
        // If canvas is tainted, use original image URL
        imageData = image?.url;
        console.log('Canvas tainted, using original URL for AI edit');
      } else {
        const canvas = canvasRef.current;
        imageData = canvas.toDataURL('image/png');
      }

      // Call existing API
      console.log('Calling AI edit API with prompt:', editPrompt);
      const response = await amplifyApi.editImage(imageData, 'edit', editPrompt);

      // Handle auth/rate limit errors from backend
      if (response.error === 'AUTH_REQUIRED') {
        openLoginModal('Please sign in to use AI image editing');
        setIsApplyingEdit(false);
        return;
      }

      if (response.error === 'RATE_LIMITED') {
        alert(response.message || 'Rate limit reached. Please try again later.');
        setIsApplyingEdit(false);
        return;
      }

      if (response.success && response.image) {
        const resultUrl = response.image.url;
        console.log('AI edit response URL:', resultUrl?.substring(0, 100));

        // Check if URL needs to be proxied (external S3/Printify URLs cause CORS issues)
        const isDataUrl = resultUrl?.startsWith('data:');
        const needsProxy = !isDataUrl && resultUrl && (
          resultUrl.includes('pfy-prod-image-storage.s3') ||
          resultUrl.includes('images-api.printify') ||
          resultUrl.includes('s3.amazonaws.com') ||
          resultUrl.includes('amazonaws.com')
        );

        const loadEditedImage = (imageUrl) => {
          const editedImg = new Image();
          editedImg.onload = () => {
            // Update originalImage with edited result (keeps baseImage for Reset)
            setOriginalImage(editedImg);
            setCanvasTainted(false);
            setEditPrompt(''); // Clear input
            setIsApplyingEdit(false);
            setAiEditApplied(true); // Mark that AI edit was applied (forces new upload on save)
            console.log('AI edit applied successfully');
          };
          editedImg.onerror = () => {
            console.error('Failed to load edited image');
            alert('Failed to load edited image. Please try again.');
            setIsApplyingEdit(false);
          };
          editedImg.src = imageUrl;
        };

        if (needsProxy) {
          // Fetch via proxy to avoid CORS issues and get data URL
          console.log('AI edited image needs proxy:', resultUrl);
          try {
            const apiEndpoint = getApiEndpoint();
            const cacheBuster = Date.now();
            const fetchUrl = `${apiEndpoint}/api/images/proxy?dataurl=true&_=${cacheBuster}&url=${encodeURIComponent(resultUrl)}`;

            const proxyResponse = await fetch(fetchUrl);
            if (!proxyResponse.ok) throw new Error(`Proxy fetch failed: ${proxyResponse.status}`);

            const proxyResult = await proxyResponse.json();
            if (!proxyResult.success || !proxyResult.dataUrl) {
              throw new Error(proxyResult.error || 'Proxy did not return data URL');
            }

            console.log('Got data URL from proxy, loading edited image');
            loadEditedImage(proxyResult.dataUrl);
          } catch (proxyErr) {
            console.error('Failed to proxy edited image:', proxyErr);
            alert('Failed to load edited image. Please try again.');
            setIsApplyingEdit(false);
          }
        } else {
          // Data URL or safe URL - load directly
          loadEditedImage(resultUrl);
        }
      } else {
        throw new Error(response.error || 'Edit failed');
      }
    } catch (err) {
      console.error('AI edit failed:', err);
      // Handle errors that come through as exceptions
      const errorData = err.response?.data || {};
      if (errorData.error === 'AUTH_REQUIRED') {
        openLoginModal('Please sign in to use AI image editing');
      } else if (errorData.error === 'RATE_LIMITED') {
        alert(errorData.message || 'Rate limit reached. Please try again later.');
      } else {
        alert('Edit failed. Please try a different description.');
      }
      setIsApplyingEdit(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* CORS Warning */}
      {canvasTainted && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <strong>Note:</strong> Some editing features are limited due to image source restrictions.
          You can still adjust settings and save, but the original image will be used.
        </div>
      )}

      {/* AI Generation Prompt Display */}
      {image?.aiPrompt && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">AI Generated</span>
              <p className="text-sm text-slate-700 mt-0.5 italic">"{image.aiPrompt}"</p>
            </div>
          </div>
        </div>
      )}

      {/* Canvas */}
      <div className="flex justify-center overflow-x-auto">
        <div className="relative bg-slate-100 rounded-xl p-2 sm:p-4 inline-block max-w-full min-h-[200px] min-w-[200px]">
          <canvas
            ref={canvasRef}
            className={`rounded-lg max-w-full h-auto ${isCropping ? 'cursor-crosshair' : ''}`}
            style={{ maxWidth: '100%', height: 'auto' }}
            onMouseDown={handleCropStart}
            onMouseMove={handleCropMove}
            onMouseUp={handleCropEnd}
            onMouseLeave={handleCropEnd}
          />
          {isImageLoading && (
            <div className="absolute inset-0 bg-slate-100 rounded-xl flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                <span className="text-slate-600 font-medium">Loading image...</span>
              </div>
            </div>
          )}
          {isRemovingBg && (
            <div className="absolute inset-0 bg-white/80 rounded-xl flex items-center justify-center">
              <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                <span className="text-slate-700 font-medium">Removing background...</span>
              </div>
            </div>
          )}
          {isApplyingEdit && (
            <div className="absolute inset-0 bg-white/80 rounded-xl flex items-center justify-center">
              <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                <span className="text-slate-700 font-medium">Applying AI edit...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Edit Section - only show if feature is enabled */}
      {aiEditEnabled && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-slate-700">AI Edit</span>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              placeholder="Describe your edit... (e.g., 'add sparkles', 'make background blue', 'add a wizard hat')"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={isApplyingEdit || canvasTainted}
              onKeyPress={(e) => e.key === 'Enter' && handleApplyEdit()}
            />
            <button
              onClick={handleApplyEdit}
              disabled={!editPrompt.trim() || isApplyingEdit || canvasTainted}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              {isApplyingEdit ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Applying...</span>
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  <span className="text-sm">Apply</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {canvasTainted
              ? "AI edit unavailable due to image source restrictions"
              : "AI will modify your image based on your description. Use Reset to undo."}
          </p>
        </div>
      )}

      {/* Tools */}
      <div className="bg-slate-50 rounded-xl p-4 space-y-4">
        {/* Quick actions */}
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            onClick={() => handleRotate(-90)}
            className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            title="Rotate Left"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="text-sm">Left</span>
          </button>
          <button
            onClick={() => handleRotate(90)}
            className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            title="Rotate Right"
          >
            <RotateCw className="w-4 h-4" />
            <span className="text-sm">Right</span>
          </button>
          {/* Crop button with dropdown for shape selection */}
          <div className="relative">
            <button
              onClick={() => setIsCropping(!isCropping)}
              disabled={canvasTainted}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors
                ${isCropping
                  ? 'bg-blue-600 text-white border-blue-600'
                  : canvasTainted
                    ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-white border-slate-200 hover:bg-slate-50'
                }
              `}
              title={canvasTainted ? "Cropping unavailable due to image restrictions" : "Crop"}
            >
              {cropMode === 'rectangle' ? <Square className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
              <span className="text-sm">{isCropping ? 'Cropping...' : 'Crop'}</span>
            </button>
          </div>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            title="Reset"
          >
            <Undo2 className="w-4 h-4" />
            <span className="text-sm">Reset</span>
          </button>
          {onRemoveBackground && bgRemovalEnabled && (
            <button
              onClick={handleRemoveBackground}
              disabled={isRemovingBg}
              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-colors disabled:opacity-50"
              title="Remove Background (AI)"
            >
              <Wand2 className="w-4 h-4" />
              <span className="text-sm">Remove BG</span>
            </button>
          )}
        </div>

        {/* Crop shape selector - shown when cropping is active */}
        {isCropping && (
          <div className="flex items-center justify-center gap-2 py-2 px-3 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm text-blue-700 font-medium mr-2">Crop Shape:</span>
            <button
              onClick={() => setCropMode('rectangle')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                cropMode === 'rectangle'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
              }`}
              title="Rectangle crop"
            >
              <Square className="w-4 h-4" />
              Rectangle
            </button>
            <button
              onClick={() => setCropMode('oval')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                cropMode === 'oval'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
              }`}
              title="Oval crop (creates transparency)"
            >
              <Circle className="w-4 h-4" />
              Oval
            </button>
            <button
              onClick={() => setCropMode('circle')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                cropMode === 'circle'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
              }`}
              title="Circle crop (1:1 ratio, creates transparency)"
            >
              <Circle className="w-4 h-4" />
              Circle
            </button>
          </div>
        )}

        {/* Download & Save */}
        <div className="flex flex-wrap gap-2 justify-center pt-2 border-t border-slate-200">
          <button
            onClick={handleDownload}
            disabled={canvasTainted}
            className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={canvasTainted ? "Download unavailable due to image restrictions" : "Download image"}
          >
            <Download className="w-4 h-4" />
            <span className="text-sm">Download</span>
          </button>
          <button
            onClick={() => requireAuth('Sign in to save your design', handleSaveDesign)}
            disabled={isSavingDesign}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors
              ${designSaved
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-white border-slate-200 hover:bg-slate-50'
              }
              disabled:opacity-50`}
            title="Save design to cloud"
          >
            {isSavingDesign ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : designSaved ? (
              <Cloud className="w-4 h-4 fill-current text-blue-500" />
            ) : (
              <Cloud className="w-4 h-4" />
            )}
            <span className="text-sm">{designSaved ? 'Saved!' : 'Save Design'}</span>
          </button>
        </div>

        {/* Sliders */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Sun className="w-4 h-4" />
              Brightness: {brightness}%
            </label>
            <input
              type="range"
              min="50"
              max="150"
              value={brightness}
              onChange={(e) => setBrightness(Number(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Contrast className="w-4 h-4" />
              Contrast: {contrast}%
            </label>
            <input
              type="range"
              min="50"
              max="150"
              value={contrast}
              onChange={(e) => setContrast(Number(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default ImageEditor;
